import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "container-xl desktop-nav-cats__inner" }
const _hoisted_3 = {
  key: 0,
  class: "desktop-dept-wrap"
}
const _hoisted_4 = ["aria-expanded"]

import { computed, inject, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router';
import clickOutsidePlugin from 'click-outside-vue3';
import { configurationKey } from '@/injections';
import { GIFT_CARD_ROUTE_NAME } from '@/constants/AppConstants';
import useEmployee from '@/modules/employee/composables/useEmployee';
import WCCustomSelectModern from '@/modules/forms/components/WCCustomSelect/WCCustomSelectModern.vue';
import WCMultiColumnMenu from '@/components/WCMultiColumnMenu/WCMultiColumnMenu.vue';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import type { MenuNode } from '@/models/entities/menu-node';

interface SaveLink {
  id: string;
  labelKey: string;
  routeName: string;
  icon: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WCDesktopSubNavbar',
  props: {
    wcHeaderHeight: { default: '0px' }
  },
  setup(__props: any) {

const vClickOutside = clickOutsidePlugin.directive;

const QUICK_ACCESS_ROUTE = {
  WEEKLY_AD: 'Circular',
  DIGITAL_COUPONS: 'Coupon Browse',
  REWARDS: 'Rewards',
  SHOPPING_LISTS: 'Lists',
  REORDER: 'Buy It Again',
};



const store = useStore();
const route = useRoute();
const router = useRouter();
const { isNotAnEmployee } = useEmployee();
const { activeOrderType, navigateToOrderTypeHome } = useOrderTypes();
const config = inject(configurationKey);

const isDeptOpen = ref(false);
const activeMenu = ref<MenuNode | null>(null);

const hasDepartments = computed(
  () =>
    route.name !== GIFT_CARD_ROUTE_NAME &&
    !activeOrderType.value?.giftCard &&
    Boolean(activeMenu.value?.children?.length),
);

const saveLinks = computed<SaveLink[]>(() => {
  const links: SaveLink[] = [];
  if (config?.includeCircularLinkInMenu) {
    links.push({
      id: 'weeklyAd',
      labelKey: 'weeklyAd',
      routeName: QUICK_ACCESS_ROUTE.WEEKLY_AD,
      icon: 'newspaper',
    });
  }
  if (config?.allowCouponClipping) {
    links.push({
      id: 'digitalCoupons',
      labelKey: 'digitalCoupons',
      routeName: QUICK_ACCESS_ROUTE.DIGITAL_COUPONS,
      icon: 'ticket',
    });
  }
  if (config?.rewardsEnabled) {
    links.push({
      id: 'rewards',
      labelKey: 'rewardsAndLoyalty',
      routeName: QUICK_ACCESS_ROUTE.REWARDS,
      icon: 'star',
    });
  }
  return links;
});

function resolveActiveMenu(cartOrderTypeId: string | null): void {
  if (cartOrderTypeId) {
    let id = cartOrderTypeId;
    if (id.startsWith('WOT-')) id = id.substring(4);
    const orderType = config?.orderTypes?.find((ot: any) => ot.id === id);
    if (orderType?.menu) {
      activeMenu.value = {
        ...orderType.menu,
        name: orderType.name,
        orderTypeId: orderType.id,
      };
      return;
    }
  }
  if (config?.orderTypesEnabled && config?.orderTypes?.length) {
    const fallback = config.orderTypes.find((ot: any) => !ot.hidden && !ot.giftCard);
    if (fallback?.menu) {
      activeMenu.value = {
        ...fallback.menu,
        name: fallback.name,
        orderTypeId: fallback.id,
      };
      return;
    }
  }
  activeMenu.value = config?.mainMenu ?? null;
}

function getSaveOptions(translate: any) {
  return saveLinks.value.map(link => ({
    id: link.id,
    name: translate(link.labelKey),
    routeName: link.routeName,
    icon: link.icon,
  }));
}

function toggleDept() {
  isDeptOpen.value = !isDeptOpen.value;
}

function closeDept() {
  isDeptOpen.value = false;
}

function navigateTo(destination: RouteLocationRaw): void {
  router.push(destination).catch(() => {});
}

function onSaveSelected(option: { routeName?: string } | null): void {
  if (option && option.routeName) {
    navigateTo({ name: option.routeName });
  }
}

function onDeptSelect({ child }: { child: MenuNode }): void {
  navigateTo({ name: 'Browse', query: { g: child.id } });
  isDeptOpen.value = false;
}

function onDeptViewAll(dept: MenuNode): void {
  navigateTo({ name: 'Browse', query: { g: dept.id } });
  isDeptOpen.value = false;
}

function onDeptVisitHome(menu: MenuNode): void {
  if (menu?.orderTypeId) {
    navigateToOrderTypeHome(menu.orderTypeId);
  } else {
    navigateTo({ name: 'Browse' });
  }
  isDeptOpen.value = false;
}

watch(
  () => store.getters['cart/getCartOrderType'],
  (cartOrderTypeId: string | null) => resolveActiveMenu(cartOrderTypeId),
  { immediate: true },
);

watch(
  () => route.fullPath,
  () => {
    isDeptOpen.value = false;
  },
);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_unref(isNotAnEmployee))
    ? _withDirectives((_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "desktop-nav-cats-wrapper",
        onKeydown: _withKeys(closeDept, ["esc"])
      }, [
        _createElementVNode("nav", {
          class: "desktop-nav-cats",
          "aria-label": _ctx.$t('subNavigation')
        }, [
          _createElementVNode("div", _hoisted_2, [
            (hasDepartments.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["desktop-cat-link primary-btn", { 'is-open': isDeptOpen.value }]),
                    "aria-haspopup": "true",
                    "aria-expanded": isDeptOpen.value ? 'true' : 'false',
                    "aria-controls": "dept-dropdown",
                    onClick: toggleDept
                  }, [
                    _createVNode(_component_font_awesome_icon, { icon: "table-cells-large" }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('departments')) + " ", 1),
                    _createVNode(WCSvgIcon, {
                      name: "chevron-down",
                      class: _normalizeClass(["dept-chevron", { 'dept-chevron--open': isDeptOpen.value }]),
                      "icon-size": { widthRem: 0.625, heightRem: 0.4375 }
                    }, null, 8, ["class"])
                  ], 10, _hoisted_4),
                  _cache[0] || (_cache[0] = _createTextVNode()),
                  _createVNode(WCMultiColumnMenu, {
                    menuData: activeMenu.value,
                    isOpen: isDeptOpen.value,
                    onSelectSubcategory: onDeptSelect,
                    onViewAll: onDeptViewAll,
                    onVisitHome: onDeptVisitHome,
                    onClose: closeDept
                  }, null, 8, ["menuData", "isOpen"])
                ]))
              : _createCommentVNode("", true),
            _cache[1] || (_cache[1] = _createTextVNode()),
            (saveLinks.value.length)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: "desktop-save-wrap",
                  onClick: closeDept
                }, [
                  _createVNode(WCCustomSelectModern, {
                    availableOptions: getSaveOptions(_ctx.$t),
                    name: _ctx.$t('save'),
                    unusableHeight: _ctx.wcHeaderHeight,
                    dropdownLabel: _ctx.$t('save'),
                    "btn-class": "desktop-cat-link",
                    "btn-icon": "tag",
                    "show-chevron": "",
                    onSelectedOption: onSaveSelected
                  }, null, 8, ["availableOptions", "name", "unusableHeight", "dropdownLabel"])
                ]))
              : _createCommentVNode("", true),
            _cache[2] || (_cache[2] = _createTextVNode()),
            (_unref(config)?.listEnabled)
              ? (_openBlock(), _createBlock(_component_router_link, {
                  key: 2,
                  class: "desktop-cat-link",
                  to: { name: QUICK_ACCESS_ROUTE.SHOPPING_LISTS }
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, { icon: "clipboard-list" }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('shoppingLists')), 1)
                  ]),
                  _: 1
                }, 8, ["to"]))
              : _createCommentVNode("", true),
            _cache[3] || (_cache[3] = _createTextVNode()),
            (_unref(config)?.pastItemsEnabled)
              ? (_openBlock(), _createBlock(_component_router_link, {
                  key: 3,
                  class: "desktop-cat-link",
                  to: { name: QUICK_ACCESS_ROUTE.REORDER }
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, { icon: "clock-rotate-left" }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('reorder')), 1)
                  ]),
                  _: 1
                }, 8, ["to"]))
              : _createCommentVNode("", true)
          ])
        ], 8, _hoisted_1)
      ], 32)), [
        [_unref(vClickOutside), closeDept]
      ])
    : _createCommentVNode("", true)
}
}

})